import { ReactNode } from 'react';
import { View, Text, Image } from 'react-native';
import { isWeb } from '@/lib/platform';
import { useResponsive } from '@/lib/useResponsive';
import { usePhoneShell } from '@/lib/useShell';
import { useSystemThemeColors } from '@/lib/useThemeColors';
import { LinearGradient } from 'expo-linear-gradient';
import { TrendingUp, Shield, Users } from 'lucide-react-native';

import { LOGO_MARK } from '@/lib/logo';

interface AuthLayoutProps {
  children: ReactNode;
}

/**
 * Layout wrapper for authentication screens
 *
 * Mobile: Renders children directly (full screen)
 * Web at phone width: full screen as well
 * Web Desktop: Split layout with branding panel + auth form
 * Web Tablet: Centered card with max-width
 */
export function AuthLayout({ children }: AuthLayoutProps) {
  const colors = useSystemThemeColors();
  const { isDesktop, isTablet } = useResponsive();
  // Width-based and false before mount, so a desktop never flashes the phone layout.
  const phoneWidth = usePhoneShell();

  // Mobile: render directly
  if (!isWeb) {
    return <>{children}</>;
  }

  // Web at phone width: full screen, like native. Phone-width web used to fall
  // through to the desktop split layout below, whose form panel is
  // `minWidth: 400` + `justify-center p-8` — wider than a 375px viewport, and
  // the centring left a large empty gap under "Create Account" (119-07 UAT).
  if (phoneWidth) {
    return (
      <View className="flex-1" style={{ backgroundColor: colors.surface.primary }}>
        {children}
      </View>
    );
  }

  // Web tablet: centered card
  if (isTablet && !isDesktop) {
    return (
      <View
        className="flex-1 items-center justify-center p-6"
        style={{ backgroundColor: colors.surface.secondary }}
      >
        <View
          className="w-full rounded-2xl overflow-hidden"
          style={{
            maxWidth: 480,
            backgroundColor: colors.surface.primary,
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.1,
            shadowRadius: 20,
          }}
        >
          {children}
        </View>
      </View>
    );
  }

  // Web desktop: split layout
  return (
    <View className="flex-1 flex-row" style={{ backgroundColor: colors.surface.secondary }}>
      {/* Left panel - Branding */}
      <View className="flex-1 hidden lg:flex">
        <LinearGradient
          colors={['#0A1628', '#132240', '#1A3058']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={{ flex: 1, padding: 48, justifyContent: 'space-between' }}
        >
          {/* Logo */}
          <View className="flex-row items-center gap-3">
            <Image
              source={LOGO_MARK}
              style={{ width: 48, height: 48 }}
              resizeMode="contain"
            />
            <Text className="text-2xl font-bold text-white">Inverio</Text>
          </View>

          {/* Value props */}
          <View className="gap-8">
            <Text className="text-4xl font-bold text-white leading-tight">
              Connect startups{'\n'}with the right{'\n'}investors
            </Text>

            <View className="gap-6">
              <FeatureItem
                icon={<TrendingUp size={24} color="white" />}
                title="Investment Readiness Score"
                description="Get an objective score based on 50+ data points"
              />
              <FeatureItem
                icon={<Shield size={24} color="white" />}
                title="Verified Investors"
                description="LinkedIn-verified investors with real track records"
              />
              <FeatureItem
                icon={<Users size={24} color="white" />}
                title="Smart Matching"
                description="AI-powered matching based on investment thesis"
              />
            </View>
          </View>

          {/* Footer */}
          <Text className="text-white/60 text-sm">
            Built in Geneva, Switzerland · Data hosted in the EU
          </Text>
        </LinearGradient>
      </View>

      {/* Right panel - Auth form */}
      <View
        className="flex-1 items-center justify-center p-8"
        style={{
          backgroundColor: colors.surface.primary,
          maxWidth: 600,
          minWidth: 400,
        }}
      >
        <View className="w-full" style={{ maxWidth: 420 }}>
          {children}
        </View>
      </View>
    </View>
  );
}

function FeatureItem({
  icon,
  title,
  description
}: {
  icon: ReactNode;
  title: string;
  description: string;
}) {
  return (
    <View className="flex-row gap-4">
      <View
        className="w-12 h-12 rounded-xl items-center justify-center"
        style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}
      >
        {icon}
      </View>
      <View className="flex-1">
        <Text className="text-white font-semibold text-lg">{title}</Text>
        <Text className="text-white/70 text-sm mt-1">{description}</Text>
      </View>
    </View>
  );
}
